<pdq-tabs-group variant="underline" [leftMargin]="'none'" (onChange)="onTabChange($event)">
  @for (tab of tabs; track tab.value) {
    <pdq-tab [label]="tab.label" [value]="tab.value" [active]="tab.value === activeTab"></pdq-tab>
  }
</pdq-tabs-group>

<div class="tab-panel" [style.display]="activeTab === 'settings' ? null : 'none'">
  <smart-cart-form-token-section [token]="usageToken"></smart-cart-form-token-section>

  <pdq-card variant="popover" color="white">
    <div class="card">
      <div class="card__header">
        <div class="card__title-block">
          <pdq-heading element="h5">Configure</pdq-heading>
        </div>
      </div>
      <pdq-divider></pdq-divider>
      <div class="card__content">
        <div class="field">
          <span class="field__label">Offer Title*</span>
          <pdq-input
            [formControl]="formGroup.controls.title"
            placeholder="Membership"
            [invalid]="formGroup.controls.title.errors?.['required']"
            invalidMsg="Add a title — it's used as the product name"></pdq-input>
        </div>

        <div class="field">
          <span class="field__label">Description</span>
          <rich-textarea
            [initialContent]="descriptionInitial"
            (contentChange)="onDescriptionChange($event)"
            [enableLinkInsert]="true"
            placeholder="Get membership and save on your order"></rich-textarea>
        </div>

        <pdq-radio-group [formControl]="formGroup.controls.pricingMethod" label="Pricing Method" alignment="vertical">
          <pdq-radio-button value="fixed" text="Fixed Rate"></pdq-radio-button>
          <pdq-radio-button value="percent" text="Percent"></pdq-radio-button>
        </pdq-radio-group>

        @if (!isPercent) {
          <div class="field">
            <pdq-input-arrows
              [formControl]="formGroup.controls.flatPrice"
              label="Flat Price"
              symbol="$"
              [precision]="2"
              [min]="0"
              [step]="0.5"
              widthSize="xxlarge"></pdq-input-arrows>
          </div>
        } @else {
          <div class="field-row">
            <pdq-input-arrows
              [formControl]="formGroup.controls.percent.controls.minPrice"
              label="Minimum price"
              symbol="$"
              [precision]="2"
              [min]="0"
              [step]="0.5"
              widthSize="fullwidth"></pdq-input-arrows>
            <pdq-input-arrows
              [formControl]="formGroup.controls.percent.controls.maxPrice"
              label="Maximum price"
              symbol="$"
              [precision]="2"
              [min]="0"
              [step]="0.5"
              widthSize="fullwidth"></pdq-input-arrows>
          </div>

          <div class="field">
            <pdq-input-arrows
              [formControl]="formGroup.controls.percent.controls.cartPercentage"
              label="Cart percentage"
              symbol="%"
              [precision]="0"
              [min]="1"
              [max]="100"
              [step]="1"
              widthSize="xxlarge"></pdq-input-arrows>
          </div>

          @if (rangeError) {
            <span class="percent-error">{{ rangeMessage }}</span>
          }
        }

        <pdq-checkbox
          [formControl]="formGroup.controls.showInCart"
          label="Show in cart after it's added"
          labelSize="small"></pdq-checkbox>

        <div class="discount-section">
          <pdq-checkbox
            [formControl]="formGroup.controls.discountConfiguration.controls.enabled"
            label="Add discount"
            labelSize="small"></pdq-checkbox>

          @if (isDiscountEnabled) {
            <div class="discount-fields" [formGroup]="formGroup.controls.discountConfiguration">
              <div class="field">
                <span class="field__label">Apply to</span>
                <pdq-dropdown
                  [formControl]="formGroup.controls.discountConfiguration.controls.applyTo"
                  [options]="discountApplyToOptions"
                  fullWidth="true"></pdq-dropdown>
              </div>

              <div class="discount-row">
                <div class="field discount-row__type">
                  <span class="field__label">Type</span>
                  <pdq-dropdown
                    [formControl]="formGroup.controls.discountConfiguration.controls.discountType"
                    [options]="discountTypeOptions"
                    fullWidth="true"></pdq-dropdown>
                </div>
                <div class="field discount-row__value">
                  <span class="field__label">Value</span>
                  <pdq-input-arrows
                    [formControl]="formGroup.controls.discountConfiguration.controls.value"
                    [min]="1"
                    [max]="
                      formGroup.controls.discountConfiguration.controls.discountType.value === 'percentage'
                        ? 100
                        : undefined
                    "
                    [precision]="2"
                    [symbol]="
                      formGroup.controls.discountConfiguration.controls.discountType.value === 'percentage'
                        ? '%'
                        : currencySymbol
                    "
                    widthSize="fullwidth"></pdq-input-arrows>
                </div>
              </div>

              <div class="discount-row">
                <div class="field discount-row__label-text">
                  <span class="field__label">
                    Discount Badge
                    @if (isDiscountAppliedToUpsellItem) {
                      <pdq-icon
                        icon="more_information"
                        size="x-large"
                        color="var(--on-surface-light)"
                        matTooltip="Shown below the product name in the Smart Cart and in Shopify checkout. Both surfaces show the same badge."
                        matTooltipClass="pdq-mat-tooltip"></pdq-icon>
                    }
                  </span>
                  <pdq-input
                    [formControl]="formGroup.controls.discountConfiguration.controls.labelText"
                    [placeholder]="discountLabelPlaceholder"></pdq-input>
                </div>
                @if (isDiscountAppliedToUpsellItem) {
                  <div class="field discount-row__label-color">
                    <span class="field__label">Label color</span>
                    <pdq-input-color
                      [formControl]="formGroup.controls.discountConfiguration.controls.labelColor"></pdq-input-color>
                  </div>
                }
              </div>
            </div>
          }
        </div>

        <pdq-checkbox
          [formControl]="formGroup.controls.autoSelectOnlyFreeItem"
          label="Auto-select only free digital item at smart cart"
          labelSize="small"></pdq-checkbox>
      </div>
    </div>
  </pdq-card>

  <pdq-card variant="popover" color="white">
    <div class="card">
      <div class="card__header">
        <div class="card__title-block">
          <pdq-heading element="h5">Appearance</pdq-heading>
        </div>
      </div>
      <pdq-divider></pdq-divider>
      <div class="card__content">
        <div class="field">
          <span class="field__label">Product Image</span>
          <pdq-image-upload-card
            variant="upload-area"
            thumbnailFit="contain"
            [imageUrl]="productImageUrl"
            [allowedImageTypes]="allowedProductImageTypes"
            [maxFileSizeBytes]="maxProductImageBytes"
            idleTitle="Drop your image file here"
            idleLinkText="or click to browse"
            formatHint="PNG, SVG, JPG"
            [validationErrorMessage]="imageValidationError"
            (onImageChange)="onProductImageChanged($event)"
            (onValidationFailure)="onProductImageValidationFailed($event)"></pdq-image-upload-card>
        </div>

        <div class="field">
          <span class="field__label">Card</span>
          <div class="appearance-row">
            <pdq-input-color
              [formControl]="formGroup.controls.appearance.controls.card.controls.backgroundColor"
              label="Background Color"></pdq-input-color>
            <pdq-input-color
              [formControl]="formGroup.controls.appearance.controls.card.controls.textColor"
              label="Text Color"></pdq-input-color>
            <pdq-input-arrows
              [formControl]="formGroup.controls.appearance.controls.card.controls.cornerRadius"
              label="Corner Radius"
              [precision]="2"
              [min]="0"
              widthSize="fullwidth"></pdq-input-arrows>
          </div>
          <div class="appearance-row">
            <pdq-input-arrows
              [formControl]="formGroup.controls.appearance.controls.card.controls.borderWidth"
              label="Border width"
              [precision]="2"
              [min]="0"
              widthSize="fullwidth"></pdq-input-arrows>
            <pdq-input-color
              [formControl]="formGroup.controls.appearance.controls.card.controls.borderColor"
              label="Border Color"></pdq-input-color>
            <pdq-input-arrows
              [formControl]="formGroup.controls.appearance.controls.card.controls.innerPadding"
              label="Inner Padding"
              [precision]="2"
              [min]="0"
              widthSize="fullwidth"></pdq-input-arrows>
          </div>
        </div>

        <div class="field">
          <span class="field__label">Image</span>
          <div class="appearance-row">
            <div class="field">
              <span class="field__label">Fit</span>
              <pdq-dropdown
                [formControl]="formGroup.controls.appearance.controls.image.controls.fit"
                [options]="imageFitOptions"
                fullWidth="true"></pdq-dropdown>
            </div>
            <pdq-input-arrows
              [formControl]="formGroup.controls.appearance.controls.image.controls.cornerRadius"
              label="Corner Radius"
              [precision]="2"
              [min]="0"
              widthSize="fullwidth"></pdq-input-arrows>
          </div>
        </div>

        <pdq-checkbox
          [formControl]="formGroup.controls.tooltip.controls.enabled"
          label="Enable tooltip for extra details"
          labelSize="small"></pdq-checkbox>

        @if (isTooltipEnabled) {
          <div class="field">
            <rich-textarea
              [initialContent]="tooltipContentInitial"
              (contentChange)="onTooltipContentChange($event)"
              [enableLinkInsert]="true"
              placeholder="Extra details for the offer"></rich-textarea>
          </div>

          <div class="field">
            <div class="appearance-row">
              <pdq-input-color
                [formControl]="formGroup.controls.tooltip.controls.appearance.controls.backgroundColor"
                label="Background Color"></pdq-input-color>
              <pdq-input-color
                [formControl]="formGroup.controls.tooltip.controls.appearance.controls.borderColor"
                label="Border Color"></pdq-input-color>
              <pdq-input-color
                [formControl]="formGroup.controls.tooltip.controls.appearance.controls.textColor"
                label="Text Color"></pdq-input-color>
            </div>
          </div>

          <div class="field">
            <div class="appearance-row">
              <pdq-input-arrows
                [formControl]="formGroup.controls.tooltip.controls.appearance.controls.borderWidth"
                label="Border width"
                [precision]="2"
                [min]="0"
                widthSize="fullwidth"></pdq-input-arrows>
              <pdq-input-arrows
                [formControl]="formGroup.controls.tooltip.controls.appearance.controls.cornerRadius"
                label="Corner Radius"
                [precision]="2"
                [min]="0"
                widthSize="fullwidth"></pdq-input-arrows>
              <pdq-input-arrows
                [formControl]="formGroup.controls.tooltip.controls.appearance.controls.innerPadding"
                label="Inner Padding"
                [precision]="2"
                [min]="0"
                widthSize="fullwidth"></pdq-input-arrows>
            </div>
          </div>
        }

        <pdq-divider></pdq-divider>

        <pdq-radio-group
          [formControl]="formGroup.controls.selectingMethod.controls.type"
          label="Selecting method"
          alignment="vertical">
          <pdq-radio-button value="toggle" text="Toggle"></pdq-radio-button>
          <pdq-radio-button value="checkbox" text="Checkbox"></pdq-radio-button>
        </pdq-radio-group>

        <div class="field-row">
          <pdq-input-color
            [formControl]="formGroup.controls.selectingMethod.controls.defaultColor"
            label="Default Color"></pdq-input-color>
          <pdq-input-color
            [formControl]="formGroup.controls.selectingMethod.controls.selectedColor"
            label="Selected Color"></pdq-input-color>
        </div>
      </div>
    </div>
  </pdq-card>

  <smart-cart-form-custom-code
    [enabledControl]="formGroup.controls.customCode.controls.enabled"
    [codeControl]="formGroup.controls.customCode.controls.code"
    [defaultCode]="customCodeStarterTemplate"
    [variables]="customCodeVariables"></smart-cart-form-custom-code>
</div>

<div class="tab-panel" [style.display]="activeTab === 'timer' ? null : 'none'">
  <smart-cart-timer-config
    [formGroup]="formGroup.controls.timer"
    [placementOptions]="placementOptions"
    [smartCartId]="smartCartId"
    [usageToken]="usageToken"
    enableSubtitle="Add urgency with a countdown in your digital upsell!"
    hideBehaviorLabel="Hide upsell"
    [previewedState]="timerPreviewState()"
    (previewStateChange)="onTimerPreviewState($event)"></smart-cart-timer-config>

  <smart-cart-form-custom-code
    [enabledControl]="formGroup.controls.customCode.controls.enabled"
    [codeControl]="formGroup.controls.customCode.controls.code"
    [defaultCode]="customCodeStarterTemplate"
    [variables]="customCodeVariables"></smart-cart-form-custom-code>
</div>

<div class="tab-panel" [style.display]="activeTab === 'display-conditions' ? null : 'none'">
  @if (displayConditionsGroup; as displayConditionsForm) {
    <smart-cart-form-display-conditions [formGroup]="displayConditionsForm"></smart-cart-form-display-conditions>
  }
</div>
